/*
 * halal-label/label-rules.js — what an Indonesian food label must say, and
 * checks that a person can act on.
 *
 * Sources (all read from the acts, see docs/halal-label.md):
 *   PerBPOM 31/2018 Label Pangan Olahan, as amended by PerBPOM 20/2021:
 *     Pasal 5 (mandatory fields + which go on the main panel), 7 (Bahasa
 *     Indonesia first), 9 (lower-case "o" >= 1 mm; product name and warnings
 *     >= 2 mm; labels <= 10 cm2: 0,75 mm), 14 (ingredient list heading),
 *     20 (sweetener warnings), 26 (net content units), 29-31 (address,
 *     "Diproduksi oleh"), 33 (kode produksi), 34-35 (kedaluwarsa), 36
 *     (BPOM RI MD/ML, P-IRT), 37 (origin of animal ingredients), 40-42
 *     (pork), 49-51 (allergens, bold + statement).
 *   PerBPOM 4/2024 Lampiran II — P-IRT number, >= 15 digits.
 *
 * Pure module: no DOM. UMD so node --test can load it.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.HalalRules = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    /* ── text size (Pasal 9) ─────────────────────────────────────────────
     * The act measures the lower-case "o": >= 1 mm for every word on the
     * label ("Arial 6 point"), >= 2 mm for the product name and warnings, and
     * on a label of <= 10 cm2 everything may go down to 0,75 mm. The checker
     * converts a type size to the height of "o" OF THE FACE ACTUALLY USED —
     * the ratio is measured from the font (render.js measurer), not assumed
     * to be Arial's. */
    var O_MIN = { body: 1.0, prominent: 2.0, tiny: 0.75 };
    var TINY_LABEL_CM2 = 10;

    function oMinFor(role, labelAreaCm2) {
        if (labelAreaCm2 != null && labelAreaCm2 <= TINY_LABEL_CM2) return O_MIN.tiny;
        return role === 'prominent' ? O_MIN.prominent : O_MIN.body;
    }

    /* ── numbers ───────────────────────────────────────────────────────── */

    function digitsOf(s) { return String(s == null ? '' : s).replace(/\D+/g, ''); }

    /* Kemendagri province codes (2 digits): the P-IRT number carries them in
       digits 4-5 (Lampiran II PerBPOM 4/2024: "kode Provinsi dan
       Kabupaten/Kota", 4 digits). Unknown code = a warning, not a refusal:
       the list changes when provinces are created (the six of Papua, 2022). */
    var PROVINCES = {
        '11': 'Aceh', '12': 'Sumatera Utara', '13': 'Sumatera Barat', '14': 'Riau', '15': 'Jambi',
        '16': 'Sumatera Selatan', '17': 'Bengkulu', '18': 'Lampung', '19': 'Kepulauan Bangka Belitung',
        '21': 'Kepulauan Riau', '31': 'DKI Jakarta', '32': 'Jawa Barat', '33': 'Jawa Tengah',
        '34': 'DI Yogyakarta', '35': 'Jawa Timur', '36': 'Banten', '51': 'Bali',
        '52': 'Nusa Tenggara Barat', '53': 'Nusa Tenggara Timur', '61': 'Kalimantan Barat',
        '62': 'Kalimantan Tengah', '63': 'Kalimantan Selatan', '64': 'Kalimantan Timur',
        '65': 'Kalimantan Utara', '71': 'Sulawesi Utara', '72': 'Sulawesi Tengah',
        '73': 'Sulawesi Selatan', '74': 'Sulawesi Tenggara', '75': 'Gorontalo', '76': 'Sulawesi Barat',
        '81': 'Maluku', '82': 'Maluku Utara', '91': 'Papua', '92': 'Papua Barat',
        '93': 'Papua Selatan', '94': 'Papua Tengah', '95': 'Papua Pegunungan', '96': 'Papua Barat Daya',
    };

    /* P-IRT number (PerBPOM 4/2024 Lampiran II): at least 15 digits —
       1 packaging code · 2-3 food kind · 4-7 province + kab/kota ·
       8-9 product no. at the IRTP · 10-13 IRTP no. in the kab/kota ·
       last two: the year the SPP-IRT expires. The sequence numbers may grow
       beyond 2 and 4 digits, so only the head and the tail are fixed. */
    function pirt(input, today) {
        var d = digitsOf(input);
        var f = [];
        var out = { digits: d, findings: f };
        if (!d.length) { f.push({ code: 'pirt_empty', level: 'error' }); out.ok = false; return out; }
        if (d.length < 15) { f.push({ code: 'pirt_short', level: 'error', n: d.length }); out.ok = false; return out; }
        var now = today instanceof Date ? today : new Date();
        var yy = parseInt(d.slice(-2), 10);
        var year = 2000 + yy;
        out.parts = {
            pack: d.slice(0, 1), kind: d.slice(1, 3), region: d.slice(3, 7),
            province: d.slice(3, 5), expYear: year,
        };
        out.province = PROVINCES[out.parts.province] || null;
        if (!out.province) f.push({ code: 'pirt_province_unknown', level: 'warn', code2: out.parts.province });
        var thisYear = now.getFullYear();
        if (year < thisYear) f.push({ code: 'pirt_expired', level: 'error', year: year });
        // SPP-IRT is issued for 5 years; an end year further out is a typo.
        else if (year > thisYear + 5) f.push({ code: 'pirt_year_far', level: 'warn', year: year });
        out.display = 'P-IRT No. ' + d.slice(0, -2) + '-' + d.slice(-2);
        out.ok = !f.some(function (x) { return x.level === 'error'; });
        return out;
    }

    /* BPOM RI MD (domestic) / ML (import), Pasal 36 PerBPOM 31/2018: the
       prefix is mandatory and must be followed by the digits of the permit. */
    function bpom(input, kind) {
        var raw = String(input || '').toUpperCase();
        var k = kind === 'ml' ? 'ML' : 'MD';
        var d = digitsOf(raw);
        var f = [];
        var out = { digits: d, findings: f };
        if (!d.length) { f.push({ code: 'bpom_empty', level: 'error' }); out.ok = false; return out; }
        var m = raw.match(/\b(MD|ML)\b/);
        if (m && m[1] !== k) f.push({ code: 'bpom_kind_mismatch', level: 'error', found: m[1], want: k });
        if (d.length !== 12) f.push({ code: 'bpom_digits', level: 'warn', n: d.length });
        out.display = 'BPOM RI ' + k + ' ' + d;
        out.ok = !f.some(function (x) { return x.level === 'error'; });
        return out;
    }

    /* Nomor Sertifikat Halal as printed by SIHALAL. We could not confirm the
       exact digit count from a primary source (docs/halal-label.md), so the
       check is what IS certain: it starts with "ID" and carries digits. */
    function shNumber(input) {
        var raw = String(input || '').toUpperCase().replace(/\s+/g, '');
        var f = [];
        var out = { value: raw, findings: f };
        if (!raw) { f.push({ code: 'sh_empty', level: 'error' }); out.ok = false; return out; }
        if (!/^ID\d{8,}$/.test(raw)) f.push({ code: 'sh_format', level: 'warn' });
        out.ok = !f.some(function (x) { return x.level === 'error'; });
        return out;
    }

    /* ── kedaluwarsa (Pasal 34-35) ─────────────────────────────────────── */

    /* shelfDays: masa simpan. Returns which date forms are lawful and the
       one to print by default. <= 3 months → day, month, year; longer →
       month and year allowed too. Bread/cake <= 24 h, wine >= 7 %, vinegar:
       no expiry — production date instead. */
    function expiryRule(shelfDays, kind) {
        if (kind === 'bread24h' || kind === 'vinegar' || kind === 'alcohol7') {
            return { exempt: true, needProductionDate: true };
        }
        if (!(shelfDays > 0)) return { need: 'shelf' };
        var threeMonths = 92;   // the act says "3 bulan"; the longest 3 months is 92 days
        return shelfDays <= threeMonths
            ? { forms: ['dmy'], dflt: 'dmy' }
            : { forms: ['dmy', 'my'], dflt: 'my' };
    }

    var MONTHS_ID = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli',
        'Agustus', 'September', 'Oktober', 'November', 'Desember'];

    /* "Baik digunakan sebelum" + date (Pasal 34(5)). Numeric by default —
       that is what UMKM print with date stamps — with the month spelled out
       as an option. */
    function expiryText(date, form, style) {
        if (!(date instanceof Date) || isNaN(date)) return '';
        var d = date.getDate(), m = date.getMonth(), y = date.getFullYear();
        var dd = (d < 10 ? '0' : '') + d, mm = (m < 9 ? '0' : '') + (m + 1);
        if (style === 'words') return (form === 'my' ? '' : d + ' ') + MONTHS_ID[m] + ' ' + y;
        return form === 'my' ? mm + '/' + y : dd + '/' + mm + '/' + y;
    }

    function addDays(date, days) {
        var r = new Date(date.getTime());
        r.setDate(r.getDate() + days);
        return r;
    }

    /* ── ingredients: allergens (Pasal 49-51 as amended by 20/2021) ──────
     * Dictionary of Indonesian (and the common English) names. Phrases are
     * matched longest-first so "susu kedelai" is soy, not milk, and
     * "kacang hijau" is nothing at all. Mapping to null = known NOT to be an
     * allergen of the act; "?" = ambiguous, the person decides. */
    var GROUPS = ['gluten', 'egg', 'fish', 'crustacean', 'mollusc', 'peanut', 'soy', 'milk', 'treenut', 'sulphite'];
    var NAME_ID = {
        gluten: 'gluten', egg: 'telur', fish: 'ikan', crustacean: 'krustasea', mollusc: 'moluska',
        peanut: 'kacang tanah', soy: 'kedelai', milk: 'susu', treenut: 'kacang pohon', sulphite: 'sulfit',
    };
    var TERMS = {
        // gluten — "serealia mengandung gluten: gandum, rye, barley, oats, spelt"
        'tepung terigu': 'gluten', 'terigu': 'gluten', 'gandum': 'gluten', 'gandum utuh': 'gluten',
        'gandum hitam': 'gluten', 'rye': 'gluten', 'barley': 'gluten', 'jelai': 'gluten', 'oat': 'gluten',
        'oats': 'gluten', 'havermut': 'gluten', 'spelt': 'gluten', 'semolina': 'gluten', 'durum': 'gluten',
        'malt': 'gluten', 'tepung roti': 'gluten', 'wheat': 'gluten', 'gluten': 'gluten', 'bulgur': 'gluten',
        // egg
        'telur': 'egg', 'kuning telur': 'egg', 'putih telur': 'egg', 'albumin': 'egg', 'egg': 'egg',
        'mayones': 'egg', 'mayonais': 'egg', 'mayonnaise': 'egg',
        // fish
        'ikan': 'fish', 'teri': 'fish', 'tuna': 'fish', 'cakalang': 'fish', 'tongkol': 'fish', 'lele': 'fish',
        'bandeng': 'fish', 'gabus': 'fish', 'kakap': 'fish', 'salmon': 'fish', 'sarden': 'fish', 'sardin': 'fish',
        'patin': 'fish', 'nila': 'fish', 'tenggiri': 'fish', 'kembung': 'fish', 'surimi': 'fish', 'bilis': 'fish',
        'wader': 'fish', 'kecap ikan': 'fish', 'fish': 'fish', 'anchovy': 'fish', 'belanak': 'fish',
        // crustacea
        'udang': 'crustacean', 'rebon': 'crustacean', 'ebi': 'crustacean', 'lobster': 'crustacean',
        'kepiting': 'crustacean', 'rajungan': 'crustacean', 'terasi': 'crustacean', 'petis udang': 'crustacean',
        'shrimp': 'crustacean', 'prawn': 'crustacean', 'crab': 'crustacean', 'krustasea': 'crustacean',
        // mollusca
        'kerang': 'mollusc', 'tiram': 'mollusc', 'saus tiram': 'mollusc', 'cumi': 'mollusc',
        'cumi-cumi': 'mollusc', 'sotong': 'mollusc', 'gurita': 'mollusc', 'siput': 'mollusc',
        'bekicot': 'mollusc', 'remis': 'mollusc', 'kijing': 'mollusc', 'simping': 'mollusc',
        'abalon': 'mollusc', 'squid': 'mollusc', 'oyster': 'mollusc', 'moluska': 'mollusc',
        // peanut
        'kacang tanah': 'peanut', 'peanut': 'peanut', 'selai kacang': 'peanut', 'bumbu kacang': 'peanut',
        // soy
        'kedelai': 'soy', 'kedele': 'soy', 'kacang kedelai': 'soy', 'soya': 'soy', 'soy': 'soy',
        'tahu': 'soy', 'tempe': 'soy', 'kecap': 'soy', 'kecap manis': 'soy', 'kecap asin': 'soy',
        'tauco': 'soy', 'susu kedelai': 'soy', 'lesitin kedelai': 'soy', 'soy lecithin': 'soy',
        // milk ("susu (termasuk laktosa)")
        'susu': 'milk', 'laktosa': 'milk', 'keju': 'milk', 'mentega': 'milk', 'butter': 'milk',
        'krim': 'milk', 'whey': 'milk', 'kasein': 'milk', 'kaseinat': 'milk', 'yogurt': 'milk',
        'yoghurt': 'milk', 'susu bubuk': 'milk', 'susu skim': 'milk', 'susu kental manis': 'milk',
        'ghee': 'milk', 'milk': 'milk', 'cheese': 'milk', 'cream': 'milk',
        // tree nuts (list of the act)
        'almond': 'treenut', 'kenari': 'treenut', 'hazelnut': 'treenut', 'walnut': 'treenut', 'pecan': 'treenut',
        'kacang pecan': 'treenut', 'kacang brazil': 'treenut', 'pistachio': 'treenut', 'pistasio': 'treenut',
        'kacang pistachio': 'treenut', 'makadamia': 'treenut', 'macadamia': 'treenut',
        'kacang macadamia': 'treenut', 'kacang queensland': 'treenut', 'kacang mede': 'treenut',
        'kacang mete': 'treenut', 'kacang mente': 'treenut', 'mede': 'treenut', 'mete': 'treenut',
        'mente': 'treenut', 'cashew': 'treenut', 'kacang kenari': 'treenut', 'kacang almond': 'treenut',
        'susu almond': 'treenut',
        // sulphites — only >= 10 mg/kg SO2 (the person confirms)
        'sulfit': 'sulphite', 'natrium sulfit': 'sulphite', 'natrium bisulfit': 'sulphite',
        'natrium metabisulfit': 'sulphite', 'kalium sulfit': 'sulphite', 'kalium bisulfit': 'sulphite',
        'kalium metabisulfit': 'sulphite', 'kalsium bisulfit': 'sulphite', 'metabisulfit': 'sulphite',
        'belerang dioksida': 'sulphite', 'sulfur dioksida': 'sulphite',
        // NOT allergens of the act — listed so a shorter term does not fire
        'kacang hijau': null, 'kacang merah': null, 'kacang panjang': null, 'kacang polong': null,
        'kacang tolo': null, 'kacang bogor': null, 'kacang koro': null, 'kacang buncis': null,
        'susu kelapa': null, 'krim kelapa': null, 'santan': null, 'mentega putih': null,
        'krimer nabati': null,
        // "kue telur gabus" is an egg snack, not snakehead fish (gabus)
        'telur gabus': 'egg',
        // ambiguous
        'kacang': '?',
    };
    var SULPHITE_E = /\b(e|ins)\s?22[0-8]\b/i;

    var _phrases = null;
    function phrases() {
        if (_phrases) return _phrases;
        _phrases = Object.keys(TERMS).sort(function (a, b) { return b.length - a.length; });
        return _phrases;
    }

    function isWordChar(ch) { return !!ch && /[a-z0-9À-ɏ]/i.test(ch); }

    /* Find allergen terms in an ingredient list. Returns matches with
       character offsets into the ORIGINAL string (so the bold runs land on
       what the person typed) and the set of groups. */
    function allergens(text) {
        var src = String(text || '');
        var low = src.toLowerCase();
        var taken = new Array(src.length);
        var out = [];
        var list = phrases();
        for (var p = 0; p < list.length; p++) {
            var term = list[p];
            var from = 0, at;
            while ((at = low.indexOf(term, from)) >= 0) {
                from = at + 1;
                var end = at + term.length;
                if (isWordChar(low[at - 1]) || isWordChar(low[end])) continue;
                var free = true;
                for (var i = at; i < end; i++) if (taken[i]) { free = false; break; }
                if (!free) continue;
                for (var j = at; j < end; j++) taken[j] = true;
                var g = TERMS[term];
                if (g === null) continue;          // claimed, but not an allergen
                out.push({ start: at, end: end, term: src.slice(at, end), group: g === '?' ? null : g, ambiguous: g === '?' });
            }
        }
        var re = new RegExp(SULPHITE_E.source, 'gi'), mm;
        while ((mm = re.exec(src))) {
            var s0 = mm.index, e0 = s0 + mm[0].length, clash = false;
            for (var k = s0; k < e0; k++) if (taken[k]) { clash = true; break; }
            if (!clash) out.push({ start: s0, end: e0, term: mm[0], group: 'sulphite', ambiguous: false });
        }
        out.sort(function (a, b) { return a.start - b.start; });
        var groups = [];
        out.forEach(function (m) { if (m.group && groups.indexOf(m.group) < 0) groups.push(m.group); });
        groups.sort(function (a, b) { return GROUPS.indexOf(a) - GROUPS.indexOf(b); });
        return { matches: out, groups: groups };
    }

    /* Ingredient text → runs [{t, b}] for drawing with the allergens in bold
       (Pasal 51(1)). `off` lists groups the person switched off (e.g. refined
       soybean oil they know carries no protein); `*word*` marks extra bold by
       hand and the asterisks are not printed. */
    function ingredientRuns(text, found, off) {
        var src = String(text || '');
        off = off || [];
        var bold = new Array(src.length);
        (found ? found.matches : []).forEach(function (m) {
            if (m.ambiguous || off.indexOf(m.group) >= 0) return;
            for (var i = m.start; i < m.end; i++) bold[i] = true;
        });
        // manual *bold*
        var manual = /\*([^*\n]+)\*/g, mm;
        var drop = new Array(src.length);
        while ((mm = manual.exec(src))) {
            drop[mm.index] = true; drop[mm.index + mm[0].length - 1] = true;
            for (var k = mm.index + 1; k < mm.index + mm[0].length - 1; k++) bold[k] = true;
        }
        var runs = [];
        var cur = null;
        for (var c = 0; c < src.length; c++) {
            if (drop[c]) continue;
            var b = !!bold[c];
            if (!cur || cur.b !== b) { cur = { t: '', b: b }; runs.push(cur); }
            cur.t += src[c];
        }
        return runs;
    }

    /* The statement next to the list (Pasal 51). 'see' is the act's first
       form; 'list' names them ("mengandung alergen: susu, kedelai") — the
       names themselves in bold. */
    function allergenStatement(groups, form) {
        if (!groups || !groups.length) return null;
        if (form === 'list') {
            var runs = [{ t: 'Mengandung alergen: ', b: false }];
            groups.forEach(function (g, i) {
                if (i) runs.push({ t: ', ', b: false });
                runs.push({ t: NAME_ID[g] || g, b: true });
            });
            runs.push({ t: '.', b: false });
            return runs;
        }
        return [{ t: 'Mengandung alergen, lihat daftar bahan yang dicetak tebal.', b: false }];
    }

    /* Shared equipment (Pasal 51(2)) — three lawful wordings. */
    function crossContact(groups, form) {
        if (!groups || !groups.length) return null;
        var names = groups.map(function (g) { return NAME_ID[g] || g; }).join(', ');
        var head = form === 'equipment' ? 'Diproduksi menggunakan peralatan yang juga memproses '
            : (form === 'dapat' ? 'Dapat mengandung ' : 'Mungkin mengandung ');
        return [{ t: head, b: false }, { t: names, b: true }, { t: '.', b: false }];
    }

    /* ── halal-relevant words in the list ─────────────────────────────── */

    var PORK = /\b(babi|lard|bacon|ham|pork|gelatin babi|minyak babi|lemak babi|kulit babi|sosis babi|char ?siu|chashu)\b/i;
    var ALCOHOL = /\b(angciu|ang ciu|arak|mirin|sake|rum|wine|anggur merah|bir|beer|brandy|vodka|whisky|whiskey|tuak|ciu|soju|alkohol)\b/i;
    // Pasal 37: an animal-derived ingredient must name the animal.
    var NEEDS_ORIGIN = /\b(gelatin|gelatine|kolagen|collagen|lemak hewani|shortening|l-sistein|l-cysteine|plasma|kaldu|enzim|rennet|monogliserida|digliserida)\b/ig;
    var ORIGIN_WORD = /(sapi|ayam|ikan|babi|kambing|domba|bebek|nabati|tumbuhan|mikroba|kerbau|unggas)/i;

    // Drinks whose NAME carries an alcohol word but contain none.
    var NOT_ALCOHOL = /\bbir pletok\b|\btanpa alkohol\b|\bbebas alkohol\b/ig;

    function halalHints(text) {
        var src = String(text || '');
        var out = [];
        var pm = src.match(PORK);
        if (pm) out.push({ code: 'ing_pork', level: 'warn', term: pm[0] });
        var am = src.replace(NOT_ALCOHOL, ' ').match(ALCOHOL);
        if (am) out.push({ code: 'ing_alcohol', level: 'warn', term: am[0] });
        var m;
        var re = new RegExp(NEEDS_ORIGIN.source, 'ig');
        while ((m = re.exec(src))) {
            // look for the animal within the next 20 characters, e.g. "gelatin (sapi)"
            var near = src.slice(m.index, m.index + m[0].length + 24);
            if (!ORIGIN_WORD.test(near)) out.push({ code: 'ing_origin', level: 'warn', term: m[0] });
        }
        return out;
    }

    /* ── mandatory content (Pasal 5) ───────────────────────────────────── */

    /* Field ids the editor uses, the clause behind each, and whether it must
       sit on the MAIN panel (Pasal 5(2); P-IRT: Lampiran II PerBPOM 4/2024
       point 3). */
    var FIELDS = [
        { id: 'name', clause: '5(1)a', main: true },
        { id: 'ingredients', clause: '5(1)b', main: false },
        { id: 'net', clause: '5(1)c', main: true },
        { id: 'producer', clause: '5(1)d', main: true },
        { id: 'halal', clause: '5(1)e', main: true },
        { id: 'production', clause: '5(1)f', main: false },
        { id: 'expiry', clause: '5(1)g', main: true },
        { id: 'permit', clause: '5(1)h', main: true },
    ];

    /* Net content text (Pasal 26): solids by weight, liquids by volume. */
    function netText(value, unit) {
        if (!(value > 0)) return '';
        var u = unit || 'g';
        var word = (u === 'ml' || u === 'L') ? 'Isi bersih' : 'Berat bersih';
        var v = String(value).replace('.', ',');
        return word + ' ' + v + ' ' + u;
    }

    return {
        O_MIN: O_MIN,
        TINY_LABEL_CM2: TINY_LABEL_CM2,
        oMinFor: oMinFor,
        PROVINCES: PROVINCES,
        pirt: pirt,
        bpom: bpom,
        shNumber: shNumber,
        expiryRule: expiryRule,
        expiryText: expiryText,
        addDays: addDays,
        GROUPS: GROUPS,
        NAME_ID: NAME_ID,
        allergens: allergens,
        ingredientRuns: ingredientRuns,
        allergenStatement: allergenStatement,
        crossContact: crossContact,
        halalHints: halalHints,
        FIELDS: FIELDS,
        netText: netText,
    };
});
